import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import NotesForm from "@/components/meetings/NotesForm";
import SummaryPane from "@/components/meetings/SummaryPane";
import TranscriptPane from "@/components/meetings/TranscriptPane";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useApproveMeetingSummary, useDiscardMeetingSummary, useGenerateMeetingSummary } from "@/hooks/use-meeting-summary-actions";
import { useMeeting, useMeetingSummary, useMeetingTranscript } from "@/hooks/use-meeting-summary";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { usePermissions } from "@/hooks/use-permissions";
import { isEmployeeUser, useUsers } from "@/hooks/use-users";
import { formatDateTimeShort } from "@/lib/format";
import { MEETING_LOCATION_LABELS } from "@/lib/meeting-labels";

/** Referatsiden for ett møte (CRM-4.6): transkripsjon til venstre, referatet til høyre. Rute /moter/:meetingId (også under /kunde/:id). */
export default function MeetingSummaryPage() {
  const { meetingId } = useParams<{ meetingId: string }>();
  const enabled = useFeatureFlag("feature_crm_leads_moter");
  const { activeUser } = useImpersonation();
  const { isAdmin } = usePermissions();
  const { data: meeting, isLoading } = useMeeting(meetingId);
  const { data: summary, isLoading: summaryLoading } = useMeetingSummary(meetingId);
  const { data: transcript, isLoading: transcriptLoading } = useMeetingTranscript(meetingId);
  const { data: users = [] } = useUsers();
  const approve = useApproveMeetingSummary();
  const discard = useDiscardMeetingSummary();
  const generate = useGenerateMeetingSummary();
  const [jumpTo, setJumpTo] = useState<{ offsetMs: number; nonce: number } | null>(null);

  if (!enabled) return <div className="page-container"><p className="text-muted-foreground">Ikke tilgjengelig ennå.</p></div>;
  if (isLoading || summaryLoading || transcriptLoading) {
    return <div className="page-container"><Skeleton className="h-96 w-full" /></div>;
  }
  if (!meeting) return <div className="page-container"><p className="text-muted-foreground">Møtet finnes ikke, eller du har ikke tilgang.</p></div>;

  const isHost = meeting.host_user_id === activeUser?.id;
  const canEdit = isHost || isAdmin;
  const ctx = { meetingId: meeting.id, customerId: meeting.customer_id };
  const party = meeting.customer?.name ?? meeting.lead?.name ?? "Ukjent";
  const owners = users.filter((u) => isEmployeeUser(u)).map((u) => ({ id: u.id, full_name: u.full_name }));
  const markedOffsets = summary ? [...summary.decisions.map((d) => d.offset_ms), ...summary.action_items.map((a) => a.offset_ms)].filter((o): o is number => o !== null) : [];
  const noTranscriptReason = meeting.location_type === "physical"
    ? "Fysiske møter har ingen transkripsjon. Skriv stikkord fra møtet, så lager vi referatet."
    : meeting.transcription_status === "none" || meeting.transcription_status === "failed"
      ? "Det finnes ingen transkripsjon fra Meet for dette møtet. Skriv stikkord fra møtet, så lager vi referatet."
      : "Transkripsjonen er ikke hentet ennå (eller er slettet). Du kan skrive notater i mellomtiden.";

  return (
    <div className="page-container animate-fade-in space-y-6">
      <header className="space-y-1">
        <Link to={meeting.customer ? `/kunde/${meeting.customer.id}` : "/salgsmuligheter/pipe"} className="text-sm underline underline-offset-4">
          {meeting.customer ? party : "Tilbake til pipen"}
        </Link>
        <h1 className="font-display text-3xl font-semibold">{meeting.title}</h1>
        <p className="text-sm text-muted-foreground">
          {formatDateTimeShort(meeting.start_at)} · {MEETING_LOCATION_LABELS[meeting.location_type]}
          {meeting.host ? ` · vert ${meeting.host.full_name}` : ""}
          {!meeting.customer ? ` · ${party} (lead)` : ""}
        </p>
        <div className="flex flex-wrap items-center gap-2 pt-1 text-xs">
          {!transcript && <span className="rounded-pill bg-muted px-2.5 py-0.5">Ingen transkripsjon</span>}
          {transcript?.participants.map((p) => (
            <span key={p.name} className="rounded-pill border border-border px-2.5 py-0.5">
              {p.name} · {p.side === "solvrev" ? "Sølvrev" : "kunde"}
            </span>
          ))}
        </div>
      </header>

      <div className={transcript ? "grid gap-6 lg:grid-cols-2" : "max-w-3xl"}>
        {transcript && <TranscriptPane transcript={transcript} jumpTo={jumpTo} markedOffsets={markedOffsets} />}
        <div className="space-y-6">
          {summary ? (
            <SummaryPane
              key={`${summary.id}:${summary.updated_at}`}
              summary={summary}
              canEdit={canEdit}
              owners={owners}
              followUpsAvailable={!!meeting.customer_id}
              pending={approve.isPending || discard.isPending || generate.isPending}
              onJump={(offsetMs) => setJumpTo({ offsetMs, nonce: Date.now() })}
              onApprove={({ summary: text, decisions, actionItems }) =>
                approve.mutate({ ...ctx, summaryId: summary.id, summary: text, decisions, actionItems })}
              onDiscard={(reason) => discard.mutate({ ...ctx, summaryId: summary.id, reason })}
              onRegenerate={transcript ? () => generate.mutate(ctx) : undefined}
            />
          ) : canEdit ? (
            transcript ? (
              <section className="space-y-3 border-[1.5px] border-foreground bg-card p-5">
                <p className="text-sm text-muted-foreground">Det finnes ingen referat ennå.</p>
                <Button onClick={() => generate.mutate(ctx)} disabled={generate.isPending}>
                  {generate.isPending ? "Lager referat ..." : "Lag referat fra transkripsjonen"}
                </Button>
              </section>
            ) : (
              <NotesForm pending={generate.isPending} reason={noTranscriptReason} onSubmit={(notes) => generate.mutate({ ...ctx, notes })} />
            )
          ) : (
            <p className="text-muted-foreground">Referatet er ikke klart ennå.</p>
          )}
        </div>
      </div>
      {!transcript && summary?.source === "transcript" && (
        <p className="text-xs text-muted-foreground">Ingen tilgang eller slettet: rå transkripsjon beholdes bare noen måneder. Referatet beholdes.</p>
      )}
    </div>
  );
}
